import React, { type FC, type PropsWithChildren, useContext } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading from '../heading/heading';
import Container from '../container/container';
import LayoutContext from '../context/layout-context';
import ResourceCard from '../cards/resource-card/resource-card';

export interface ResourceCardsSectionProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Heading for Resource Cards section */
  sectionHeading?: string;
  /** The cards to be shown in the section container. */
  resourceCards: Lucid.Directus.Generated.Resource_Card[];
}

const ResourceCardsSection: FC<PropsWithChildren<ResourceCardsSectionProps>> = ({
  className,
  sectionHeading,
  resourceCards,
  ...rest
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_ct_consideration_faq_link_text'],
    shortCode,
  });

  return (
    <Box
      {...rest}
      as="section"
      css={theme => ({
        label: 'resourceCards',
        backgroundColor: theme.colors.coolGrey[5],
        padding: `${theme.space.s104}px 0`,
        width: 'inherit',
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Heading as="h2" css={{ textAlign: 'center' }} variant="headingXL">
        {sectionHeading}
      </Heading>
      <Container
        css={theme => ({
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: theme.space.s24,
          marginTop: theme.space.s64,
          flexWrap: 'wrap',
          [theme.breakpoints.bp768.min]: {
            flexWrap: resourceCards?.length > 3 ? 'wrap' : 'nowrap',
          },
        })}
        extraPadding={3}
      >
        {resourceCards.map((card, index) => (
          <ResourceCard
            key={`resourceCard-${index + 1}`}
            heading={card.heading}
            link={{
              default_link_text: card.link?.default_link_text || withSmartling('mktg_ct_consideration_faq_link_text'),
              final_url: card.link?.final_url,
              accessibleDescription: `${card.heading} - ${card.link?.default_link_text || withSmartling('mktg_ct_consideration_faq_link_text')}`,
            }}
            text={{ value: card.text }}
          />
        ))}
      </Container>
    </Box>
  );
};

export { ResourceCardsSection };
export default ResourceCardsSection;
